import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Section, Eyebrow, SectionTitle } from "@/components/Primitives";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { ShieldCheck, ShieldAlert, FileText, Users, Briefcase, Star } from "lucide-react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

const Admin = () => {
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const [overview, setOverview] = useState(null);
  const [pending, setPending] = useState([]);

  useEffect(() => { if (!loading && (!user || !user.is_admin)) navigate("/"); }, [user, loading, navigate]);

  const load = () => {
    api.get("/admin/overview").then((r) => setOverview(r.data)).catch(() => {});
    api.get("/admin/verifications").then((r) => setPending(r.data)).catch(() => {});
  };
  useEffect(() => { if (user?.is_admin) load(); }, [user]);

  const act = async (uid, action) => {
    try {
      await api.post(`/admin/verifications/${uid}`, { action });
      toast.success(action === "approve" ? "Contractor approved & verified" : "Contractor rejected");
      load();
    } catch (e) { toast.error("Action failed"); }
  };

  if (!user?.is_admin) return null;

  return (
    <div>
      <div className="eti-grid-bg border-b border-white/10 py-12">
        <Section><Eyebrow>Back Office</Eyebrow><h1 className="font-display text-3xl sm:text-4xl font-extrabold uppercase tracking-tight text-white">Admin Panel</h1></Section>
      </div>
      <Section className="py-10">
        {overview && (
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-10">
            {[[Users, overview.users, "Total Users"], [ShieldAlert, overview.pending_verifications, "Pending Reviews"], [Briefcase, overview.jobs, "Jobs Posted"], [Star, overview.reviews, "Reviews"]].map(([Icon, n, l], i) => (
              <div key={i} className="eti-card rounded-lg p-5" data-testid={`admin-stat-${i}`}><Icon className="h-6 w-6 text-primary" /><p className="font-display text-3xl font-bold text-white mt-2">{n}</p><p className="text-xs text-slate-400">{l}</p></div>
            ))}
          </div>
        )}
        <SectionTitle>Verification Queue</SectionTitle>
        <div className="mt-6 space-y-4">
          {pending.length === 0 && <div className="eti-card rounded-lg p-8 text-center text-slate-400" data-testid="admin-empty-queue">No contractors awaiting review.</div>}
          {pending.map((c) => (
            <div key={c.user_id} className="eti-card rounded-lg p-5" data-testid={`admin-verification-${c.user_id}`}>
              <div className="flex flex-wrap items-center justify-between gap-3">
                <div>
                  <p className="font-display font-bold text-white">{c.name}</p>
                  <p className="text-xs text-slate-400">{c.email} · {c.company || "—"}</p>
                </div>
                <Badge className={c.verification_status === "rejected" ? "bg-red-500/15 text-red-400 border-0" : "bg-amber-500/15 text-amber-400 border-0"}>{c.verification_status}</Badge>
              </div>
              <div className="flex flex-wrap gap-3 mt-4">
                {["wsib", "insurance"].map((t) => (
                  c.verification_docs?.[t] ? (
                    <a key={t} href={c.verification_docs[t].url ? `${BACKEND}${c.verification_docs[t].url}` : "#"} target="_blank" rel="noreferrer" className="flex items-center gap-2 text-sm text-slate-200 border border-white/10 rounded-md px-3 py-1.5 hover:border-primary" data-testid={`admin-doc-${t}-${c.user_id}`}>
                      <FileText className="h-4 w-4 text-primary" />{t.toUpperCase()} Document
                    </a>
                  ) : <span key={t} className="text-xs text-slate-500 px-3 py-1.5 border border-white/10 rounded-md">{t.toUpperCase()}: missing</span>
                ))}
              </div>
              <div className="flex gap-2 mt-4">
                <Button size="sm" onClick={() => act(c.user_id, "approve")} className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid={`admin-approve-${c.user_id}`}><ShieldCheck className="h-4 w-4 mr-1" />Approve</Button>
                <Button size="sm" variant="outline" onClick={() => act(c.user_id, "reject")} className="border-white/15 text-white" data-testid={`admin-reject-${c.user_id}`}><ShieldAlert className="h-4 w-4 mr-1" />Reject</Button>
              </div>
            </div>
          ))}
        </div>
      </Section>
    </div>
  );
};

export default Admin;
